import moment from "moment";
import { parseFechaFlexible } from "./usuarios";
import { resolverEstadoMembresia, PRECIO_LISTA_MXN, DIAS_POR_VENCER } from "./suscripciones";

/**
 * Cálculo de métricas. Funciones puras sobre los datos ya normalizados: no
 * tocan Firestore ni la red, así que se pueden recalcular al vuelo cada vez que
 * cambia el filtro de fechas.
 */

/** Días que puede estar un registro sin completar perfil antes de considerarse estancado. */
export const DIAS_REGISTRO_ESTANCADO = 7;

export function enRango(fecha, rango) {
  if (!fecha || !fecha.isValid()) return false;
  if (!rango) return true;
  if (rango.desde && fecha.isBefore(rango.desde)) return false;
  if (rango.hasta && fecha.isAfter(rango.hasta)) return false;
  return true;
}

/** Mismo número de días que `rango`, pero inmediatamente anterior. */
export function rangoAnterior(rango) {
  if (!rango || !rango.desde || !rango.hasta) return null;
  const dias = rango.hasta.diff(rango.desde, "days") + 1;
  return {
    desde: rango.desde.clone().subtract(dias, "days"),
    hasta: rango.desde.clone().subtract(1, "millisecond"),
  };
}

export function variacion(actual, anterior) {
  if (!anterior) return actual > 0 ? 100 : 0;
  return Math.round(((actual - anterior) / anterior) * 100);
}

export function porcentaje(parte, total) {
  if (!total) return 0;
  return Math.round((parte / total) * 100);
}

/** Estado de membresía de un usuario según el índice masivo de Stripe. */
export function membresiaDe(usuario, indice) {
  return resolverEstadoMembresia(usuario.email ? indice[usuario.email] : null);
}

/**
 * KPIs principales. `rango` filtra las altas; los totales acumulados (padrón
 * completo, suscriptores vigentes hoy) no dependen del rango por definición.
 */
export function calcularResumen({ usuarios, negocios, sesiones, vacantes, eventos, indice }, rango) {
  const enPeriodo = usuarios.filter((u) => enRango(u.creadoEn, rango));
  const anterior = rangoAnterior(rango);
  const enPeriodoAnterior = anterior
    ? usuarios.filter((u) => enRango(u.creadoEn, anterior))
    : [];

  const completados = usuarios.filter((u) => u.estadoRegistro === "completado").length;
  const activos = usuarios.filter((u) => membresiaDe(u, indice).activo).length;

  const negociosEnPeriodo = negocios.filter((n) =>
    enRango(parseFechaFlexible(n.register_date), rango)
  );

  return {
    registrosTotal: usuarios.length,
    registrosPeriodo: enPeriodo.length,
    registrosVariacion: variacion(enPeriodo.length, enPeriodoAnterior.length),

    perfilesCompletados: completados,
    tasaCompletitud: porcentaje(completados, usuarios.length),

    suscriptoresActivos: activos,
    tasaConversion: porcentaje(activos, usuarios.length),
    mrrEstimado: activos * PRECIO_LISTA_MXN,

    negociosTotal: negocios.length,
    negociosPeriodo: negociosEnPeriodo.length,

    sesionesTotal: sesiones.length,
    vacantesActivas: vacantes.filter((v) => v.active !== false).length,
    eventosActivos: eventos.filter((e) => e.activo).length,
  };
}

/**
 * Serie de altas agrupadas por día, semana o mes.
 * Devuelve `{ categorias, valores }` listo para ApexCharts.
 */
export function serieRegistros(usuarios, rango, granularidad = "day") {
  const formato = { day: "YYYY-MM-DD", week: "GGGG-[W]WW", month: "YYYY-MM" }[granularidad];
  const conteo = new Map();

  for (const u of usuarios) {
    if (!enRango(u.creadoEn, rango)) continue;
    const clave = u.creadoEn.format(formato);
    conteo.set(clave, (conteo.get(clave) || 0) + 1);
  }

  const claves = Array.from(conteo.keys()).sort();
  return {
    categorias: claves.map((c) => etiquetaPeriodo(c, granularidad)),
    valores: claves.map((c) => conteo.get(c)),
  };
}

function etiquetaPeriodo(clave, granularidad) {
  if (granularidad === "month") return moment(clave, "YYYY-MM").format("MMM YYYY");
  if (granularidad === "week") return clave.replace("-W", " sem ");
  return moment(clave, "YYYY-MM-DD").format("DD/MM");
}

/**
 * Embudo del alta: cuánta gente se queda en cada escalón.
 */
export function calcularEmbudo(usuarios, indice, rango) {
  const base = usuarios.filter((u) => enRango(u.creadoEn, rango));
  const completados = base.filter((u) => u.estadoRegistro === "completado");
  const suscritos = base.filter((u) => membresiaDe(u, indice).activo);

  return [
    { etapa: "Se registraron", valor: base.length },
    { etapa: "Completaron perfil", valor: completados.length },
    { etapa: "Tienen suscripción", valor: suscritos.length },
  ];
}

/**
 * Conteo por un campo ya normalizado (tipo, metodoAlta, ciudad, giro…).
 * `limite` recorta a los N más frecuentes y agrupa el resto en "Otros".
 */
export function distribucion(items, campo, { limite = null, etiquetas = {} } = {}) {
  const conteo = new Map();
  for (const it of items) {
    const clave = it[campo] || "Sin definir";
    conteo.set(clave, (conteo.get(clave) || 0) + 1);
  }

  let pares = Array.from(conteo.entries()).sort((a, b) => b[1] - a[1]);

  if (limite && pares.length > limite) {
    const resto = pares.slice(limite).reduce((acc, [, v]) => acc + v, 0);
    pares = pares.slice(0, limite);
    if (resto) pares.push(["Otros", resto]);
  }

  return {
    categorias: pares.map(([k]) => etiquetas[k] || k),
    valores: pares.map(([, v]) => v),
  };
}

/**
 * Índice de referidos: código de referidor → usuarios que lo usaron.
 */
export function construirIndiceReferidos(usuarios) {
  const idx = {};
  for (const u of usuarios) {
    const codigo = (u.used_referral_code || "").toLowerCase();
    if (!codigo) continue;
    if (!idx[codigo]) idx[codigo] = [];
    idx[codigo].push(u);
  }
  return idx;
}

/**
 * Ranking de referidores. Ordena por referidos que efectivamente pagan, que es
 * lo que importa para el programa; los referidos totales sólo desempatan.
 */
export function topReferidores(usuarios, indice, limite = 10) {
  const idxReferidos = construirIndiceReferidos(usuarios);

  return usuarios
    .map((u) => {
      const codigo = (u.referral_code || "").toLowerCase();
      const lista = codigo ? idxReferidos[codigo] || [] : [];
      const conPlus = lista.filter((r) => membresiaDe(r, indice).activo).length;
      return {
        id: u.id,
        name: u.name,
        email: u.email,
        phone: u.phone,
        referral_code: u.referral_code,
        referidosTotal: lista.length,
        referidosPlus: conPlus,
        tasa: porcentaje(conPlus, lista.length),
      };
    })
    .filter((r) => r.referidosTotal > 0)
    .sort((a, b) => b.referidosPlus - a.referidosPlus || b.referidosTotal - a.referidosTotal)
    .slice(0, limite);
}

/** Suscripciones que vencen dentro de los próximos `dias` días. */
export function suscripcionesPorVencer(usuarios, indice, dias = DIAS_POR_VENCER) {
  return usuarios
    .map((u) => ({ usuario: u, membresia: membresiaDe(u, indice) }))
    .filter(
      ({ membresia }) =>
        membresia.activo &&
        membresia.diasRestantes !== null &&
        membresia.diasRestantes >= 0 &&
        membresia.diasRestantes <= dias
    )
    .sort((a, b) => a.membresia.diasRestantes - b.membresia.diasRestantes)
    .map(({ usuario, membresia }) => ({
      id: usuario.id,
      name: usuario.name,
      email: usuario.email,
      phone: usuario.phone,
      vigencia: membresia.vigencia,
      diasRestantes: membresia.diasRestantes,
      etiqueta: membresia.etiqueta,
      variante: membresia.variante,
    }));
}

/**
 * Altas que nunca completaron el perfil. Son cuentas de Firebase creadas que se
 * quedaron a medio camino: la lista accionable para volver a contactarlas.
 */
export function registrosEstancados(usuarios, dias = DIAS_REGISTRO_ESTANCADO) {
  const limite = moment().subtract(dias, "days");
  return usuarios
    .filter(
      (u) =>
        u.estadoRegistro !== "completado" &&
        u.creadoEn &&
        u.creadoEn.isValid() &&
        u.creadoEn.isBefore(limite)
    )
    .sort((a, b) => b.creadoEn - a.creadoEn)
    .map((u) => ({
      id: u.id,
      name: u.name,
      email: u.email,
      phone: u.phone,
      creadoEnFormatted: u.creadoEnFormatted,
      diasSinCompletar: moment().diff(u.creadoEn, "days"),
    }));
}
